'use client';

import { faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { cn } from '~/lib/cn';
import { AutoSaveStatus, useAutoSaveStatus } from './AutoSaveStatusContext';

const LABELS: Record<AutoSaveStatus, string> = {
    idle: 'panel.autoSaved',
    saving: 'panel.autoSaveSaving',
    saved: 'panel.autoSaveSaved',
    error: 'panel.autoSaveError'
};

const HINTS: Record<AutoSaveStatus, string> = {
    idle: 'panel.autoSavedDelay',
    saving: 'panel.autoSavedDelay',
    saved: 'panel.autoSavedDelay',
    error: 'panel.autoSaveErrorHint'
};

/**
 * "Automatisch gespeichert", wherever a form saves on a pause in typing.
 *
 * Lives here rather than inside SettingsPanelLayout because the panel is no longer
 * the only shape a settings screen takes: a screen short enough to fit on one page
 * has no left rail to put this in, and the wording must not fork between the two.
 * It doubles as the save indicator, which is why auto-saving raises no toast.
 *
 * `unsaved` is for a screen that saves on a button instead. Passed at all, it switches
 * the wording to that screen's truth: "Automatisch gespeichert · Nach kurzer
 * Verzögerung" was a promise such a screen does not keep, and read next to a pending
 * change it told people their work was safe when it was not. There the line says
 * whether a change is waiting, and what the last press of the button came to — and
 * nothing at all while there is nothing to report.
 */
export function AutoSaveIndicator({
    className,
    inline = false,
    unsaved
}: {
    className?: string;
    inline?: boolean;
    /** Set on a screen saved by a button: whether it holds a change the server does not have. */
    unsaved?: boolean;
}) {
    const { t } = useTranslation('settings');
    const { status } = useAutoSaveStatus();
    const failed = status === 'error';
    const byButton = unsaved !== undefined;

    // A save in flight or a failure outranks a pending change; a fresh "Gespeichert"
    // does not, because an edit made after it is what matters now.
    const label = !byButton
        ? LABELS[status]
        : status === 'saving' || failed
          ? LABELS[status]
          : unsaved
            ? 'panel.unsavedChanges'
            : status === 'saved'
              ? LABELS.saved
              : null;
    const hint = !byButton ? HINTS[status] : failed ? HINTS.error : null;

    if (!label) return null;

    return (
        <div className={cn('tw-flex tw-items-center tw-gap-2', className)}>
            <FontAwesomeIcon
                icon={faCloudArrowUp}
                className={cn('tw-text-xs', failed ? 'tw-text-error' : 'tw-text-accents-7')}
            />
            <div
                className={cn(
                    'tw-flex tw-min-w-0',
                    inline ? 'tw-items-baseline tw-gap-1.5' : 'tw-flex-col tw-gap-0.5 tw-leading-none'
                )}
            >
                <span className={cn('tw-truncate tw-text-xs', failed ? 'tw-text-error' : 'tw-text-accents-8')}>
                    {t(label)}
                </span>
                {hint && (
                    <span className={cn('tw-truncate tw-text-accents-7', inline ? 'tw-text-[11px]' : 'tw-text-[10px]')}>
                        {t(hint)}
                    </span>
                )}
            </div>
        </div>
    );
}
